import { cn } from '@/lib/cn';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

export const CardsContainer = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 bg-[linear-gradient(203.06deg,#3E75D9_-37.13%,#213E73_96.85%)] px-4 py-10 lg:rounded-3xl lg:p-10',
        className,
      )}
      {...props}
    />
  );
};

export const Card = ({
  className,
  asChild = false,
  enableHover = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  asChild?: boolean;
  enableHover?: boolean;
}) => {
  const hoverElement = enableHover ? (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-active:opacity-100"
      aria-hidden="true"
    >
      <div className="absolute -top-12 -right-8 h-[200px] w-[280px] rounded-full bg-[radial-gradient(ellipse_at_center,#42DDFF_0%,#1170FF5C_50%,transparent_80%)] blur-[40px]" />
    </div>
  ) : null;

  const clonedChild = asChild
    ? (() => {
        const child = React.Children.only(children);
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            children: (
              <>
                {hoverElement}
                {(child.props as { children?: React.ReactNode }).children}
              </>
            ),
          } as Partial<unknown>);
        }
        return child;
      })()
    : null;

  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground group relative flex flex-col gap-5 overflow-hidden rounded-[0.75rem] py-7.5 transition-all lg:pt-5 lg:pb-6.5',
        className,
      )}
      {...props}
    >
      {asChild ? (
        clonedChild
      ) : (
        <>
          {hoverElement}
          {children}
        </>
      )}
    </Comp>
  );
};

export const CardHeader = ({
  className,
  historyCard = false,
  ...props
}: React.ComponentProps<'div'> & { historyCard?: boolean }) => {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header flex flex-col items-start gap-1.5',
        historyCard ? 'px-0' : 'px-7.5 lg:px-5',
        className,
      )}
      {...props}
    />
  );
};

export const CardTitle = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="card-title"
      className={cn('flex items-center gap-2.5 text-xl leading-9', className)}
      {...props}
    />
  );
};

export const CardDescription = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="card-description"
      className={cn('text-muted-foreground text-base', className)}
      {...props}
    />
  );
};

export const CardAction = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  );
};

export const CardContent = ({
  className,
  historyCard = false,
  ...props
}: React.ComponentProps<'div'> & { historyCard?: boolean }) => {
  return (
    <div
      data-slot="card-content"
      className={cn(historyCard ? 'px-0' : 'px-7.5 lg:px-5', className)}
      {...props}
    />
  );
};

export const CardFooter = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  );
};
